import React, { createContext, ProviderProps, ReactElement, useCallback, useContext, useMemo } from 'react';
import { ConsoleLogger } from './console-logger';

export interface Logger {
  /**
   * Fine-grained information to help development. Disabled in PROD.
   */
  debug(message: string | Error, details?: Record<string, unknown>): void;

  /**
   * Informational, important state changes or updates.
   */
  info(message: string | Error, details?: Record<string, unknown>): void;

  /**
   * Unexpected conditions that don't impede the application.
   */
  warn(message: string | Error, details?: Record<string, unknown>): void;

  /**
   * A component failed and an error message is shown.
   */
  error(message: string | Error, details?: Record<string, unknown>): void;

  /**
   * A critical error occurred and the user is unable to continue.
   */
  fatal(message: string | Error, details?: Record<string, unknown>): void;
}

const LoggerContext = createContext<Logger[]>([new ConsoleLogger()]);

export function useLogger(): Logger {
  const logger = useContext(LoggerContext);
  const log = useCallback(
    (level: keyof Logger) =>
      (message: string | Error, details?: Record<string, unknown>): void => {
        for (const l of logger) {
          l[level](message, details);
        }
      },
    [logger],
  );
  return useMemo(
    () => ({
      debug: log('debug'),
      info: log('info'),
      warn: log('warn'),
      error: log('error'),
      fatal: log('fatal'),
    }),
    [log],
  );
}

type LoggerProviderProps = Omit<ProviderProps<typeof LoggerContext.Provider>, 'value'> & { logger?: Logger | Logger[] };

/**
 * Provides logging capabilities. By default, logs to backend and if in debug mode, also logs to console.
 *
 * @param logger
 * @param props
 */
export function LoggerProvider({ logger, ...props }: Readonly<LoggerProviderProps>): ReactElement<LoggerProviderProps> {
  const loggerValue = useMemo(() => {
    let arr = logger ?? [];
    if (!Array.isArray(arr)) {
      arr = [arr];
    }
    if (process.env.NODE_ENV === 'development') {
      arr.push(new ConsoleLogger());
    }
    return arr;
  }, [logger]);

  return <LoggerContext.Provider value={loggerValue} {...props} />;
}
